/**
 * vender.js — Seção "Vender seu Carro"
 *
 * - Máscaras para telefone e KM
 * - Validação inline (mesmo padrão do modal de test drive)
 * - Envio via registrarLeadPublico() com origem: 'Vender'
 * - Link WhatsApp atualizado com a config da loja
 * - Estado de sucesso + botão "Enviar outro"
 */

import { registrarLeadPublico } from '../services/leads.js';

// ── Helpers DOM ────────────────────────────────────────────────
const $ = id => document.getElementById(id);

// ── Máscara de telefone ────────────────────────────────────────
function mascaraTelefone(input) {
  input.addEventListener('input', () => {
    let v = input.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 6) v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    else if (v.length > 0) v = `(${v}`;
    input.value = v;
  });
}

// ── Máscara de KM (formata com pontos: 45.000) ─────────────────
function mascaraKm(input) {
  input.addEventListener('input', () => {
    let raw = input.value.replace(/\D/g, '');
    if (raw === '') { input.value = ''; return; }
    input.value = Number(raw).toLocaleString('pt-BR') + ' km';
    // Move cursor para antes do " km"
    const pos = input.value.length - 3;
    input.setSelectionRange(pos, pos);
  });

  // Limpa " km" ao focar para facilitar edição
  input.addEventListener('focus', () => {
    input.value = input.value.replace(/\D/g, '');
  });

  input.addEventListener('blur', () => {
    const raw = input.value.replace(/\D/g, '');
    if (raw) input.value = Number(raw).toLocaleString('pt-BR') + ' km';
  });
}

// ── Validação ──────────────────────────────────────────────────
function setError(inputId, errorId, msg) {
  const el  = $(inputId);
  const err = $(errorId);
  if (!el || !err) return;
  if (msg) {
    el.classList.add('vender-invalid');
    err.textContent = msg;
  } else {
    el.classList.remove('vender-invalid');
    err.textContent = '';
  }
}

function clearAllErrors() {
  ['vender-nome', 'vender-telefone', 'vender-marca', 'vender-modelo', 'vender-ano', 'vender-km'].forEach(id => {
    $(id)?.classList.remove('vender-invalid');
  });
  ['verr-nome', 'verr-telefone', 'verr-marca', 'verr-modelo', 'verr-ano', 'verr-km'].forEach(id => {
    const el = $(id); if (el) el.textContent = '';
  });
}

function validate() {
  clearAllErrors();
  let valid = true;

  const nome     = $('vender-nome')?.value.trim()     ?? '';
  const telefone = $('vender-telefone')?.value.trim() ?? '';
  const marca    = $('vender-marca')?.value.trim()    ?? '';
  const modelo   = $('vender-modelo')?.value.trim()   ?? '';
  const ano      = $('vender-ano')?.value.trim()      ?? '';
  const kmRaw    = $('vender-km')?.value.replace(/\D/g, '') ?? '';

  if (!nome) {
    setError('vender-nome', 'verr-nome', 'Nome é obrigatório.');
    valid = false;
  }

  const rawPhone = telefone.replace(/\D/g, '');
  if (!rawPhone || rawPhone.length < 10) {
    setError('vender-telefone', 'verr-telefone', 'Informe um WhatsApp válido.');
    valid = false;
  }

  if (!marca) {
    setError('vender-marca', 'verr-marca', 'Informe a marca do veículo.');
    valid = false;
  }

  if (!modelo) {
    setError('vender-modelo', 'verr-modelo', 'Informe o modelo do veículo.');
    valid = false;
  }

  const anoNum = Number(ano);
  if (!ano || isNaN(anoNum) || anoNum < 1960 || anoNum > new Date().getFullYear() + 1) {
    setError('vender-ano', 'verr-ano', 'Informe um ano válido.');
    valid = false;
  }

  if (!kmRaw) {
    setError('vender-km', 'verr-km', 'Informe a quilometragem.');
    valid = false;
  }

  return valid;
}

// ── Estado de sucesso / reset ──────────────────────────────────
function mostrarSucesso() {
  const formState = $('vender-form-state');
  const sucesso   = $('vender-sucesso');
  if (formState) formState.hidden = true;
  if (sucesso)   sucesso.classList.remove('vender-sucesso--hidden');
}

function resetarFormulario() {
  const form = $('form-vender');
  const btn  = $('btn-vender-submit');
  const formState = $('vender-form-state');
  const sucesso   = $('vender-sucesso');

  form?.reset();
  clearAllErrors();

  if (btn) {
    btn.disabled    = false;
    btn.textContent = 'Solicitar Avaliação';
  }
  if (formState) formState.hidden = false;
  if (sucesso)   sucesso.classList.add('vender-sucesso--hidden');
}

// ── Link WhatsApp ──────────────────────────────────────────────
/**
 * Atualiza o href do botão WhatsApp da seção vender.
 * Chamado de configuracoes.js (applyContactInfo) via data-config="whatsapp_href".
 * Também pode ser chamado diretamente se a config já estiver disponível.
 * @param {string} numero - Número do WhatsApp sem formatação
 */
export function atualizarWhatsappVender(numero) {
  const btn = $('btn-vender-whatsapp');
  if (!btn || !numero) return;
  const clean = numero.replace(/\D/g, '');
  btn.href = `https://wa.me/55${clean}?text=${encodeURIComponent('Olá! Tenho um veículo para vender e gostaria de uma avaliação.')}`;
}

// ── Submit ─────────────────────────────────────────────────────
async function handleSubmit(e) {
  e.preventDefault();
  if (!validate()) return;

  const btn    = $('btn-vender-submit');
  const nome   = $('vender-nome').value.trim();
  const tel    = $('vender-telefone').value.replace(/\D/g, '');
  const marca  = $('vender-marca').value.trim();
  const modelo = $('vender-modelo').value.trim();
  const ano    = $('vender-ano').value.trim();
  const kmRaw  = $('vender-km').value.replace(/\D/g, '');
  const km     = Number(kmRaw).toLocaleString('pt-BR') + ' km';
  const obs    = $('vender-mensagem')?.value.trim() || null;

  btn.disabled    = true;
  btn.textContent = 'Enviando...';

  const mensagem = [
    `Veículo: ${marca} ${modelo} ${ano}`,
    `Quilometragem: ${km}`,
    obs ? `Observações: ${obs}` : null,
  ].filter(Boolean).join('\n');

  const payload = {
    nome,
    telefone: tel,
    mensagem,
    origem: 'Vender',
  };

  const { data, error } = await registrarLeadPublico(payload);

  if (error || !data?.ok) {
    console.error('[vender] Erro ao enviar lead:', error || data);
    btn.disabled    = false;
    btn.textContent = 'Solicitar Avaliação';
    alert('Ocorreu um erro ao enviar. Por favor, tente novamente ou entre em contato pelo WhatsApp.');
    return;
  }

  mostrarSucesso();
}

// ── Inicialização ──────────────────────────────────────────────
export function initVender() {
  const form = $('form-vender');
  if (!form) return;

  // Máscaras
  mascaraTelefone($('vender-telefone'));
  mascaraKm($('vender-km'));

  // Live-clear de erros
  ['vender-nome', 'vender-telefone', 'vender-marca', 'vender-modelo', 'vender-ano', 'vender-km'].forEach(id => {
    $(id)?.addEventListener('input', () => {
      $(id)?.classList.remove('vender-invalid');
      const errId = id.replace('vender-', 'verr-');
      const errEl = $(errId);
      if (errEl) errEl.textContent = '';
    });
  });

  // Submit
  form.addEventListener('submit', handleSubmit);

  // Botão "Enviar outro veículo"
  $('btn-vender-nova')?.addEventListener('click', resetarFormulario);
}
